import XAxisTicksWithYear from "@components/Charts/XAxisTicksWithYear";
import { DailyMetric } from "@lib/api-hooks";
import useFormat from "@lib/hooks/use-format";
import { dashboardModeAtom } from "@/atoms";
import { Granularity } from "@lib/api-hooks/useKpiBreakdownForChannel";
import {
  AXIS_VALUES,
  METRICS_LABELS,
  FINE_GRAINED_AXIS_VALUES,
  GENERAL_METRICS_LABELS,
} from "@/constants/constants";
import dayjs from "dayjs";
import { useAtom } from "jotai";
import {
  useDateRangeState,
  useNewVsReturningState,
} from "@lib/hooks/url-state";
import React, { useEffect, useMemo, useState } from "react";
import { useIsMobile } from "@tracify/ui";
import {
  CartesianGrid,
  ComposedChart,
  Label,
  Line,
  XAxis,
  YAxis,
} from "recharts";
import { formatGranularityTooltipLabel } from "@lib/util-functions";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ComparisonChartLegendContent,
  ComparisonChartTooltipContent,
} from "@tracify/ui/chart";
import { alignComparisonDataByIndex } from "@lib/util-functions/comparisonBuckets";

export const calculateRoundFactor = (maxValue: number) => {
  const numberCount = `${maxValue}`.split(".")[0].length;
  const factor = Math.max(0, numberCount - 1);
  const base =
    maxValue > 40000 ? 10 : maxValue > 10000 ? 8 : maxValue > 100 ? 5 : 2;
  const roundFactor = Math.pow(base, factor);
  return roundFactor;
};

export const getNearestAxisValue = (maxValue: number) => {
  let closest = AXIS_VALUES.filter((el) => el > maxValue).reduce(function (
    prev,
    curr
  ) {
    return Math.abs(curr - maxValue) < Math.abs(prev - maxValue) ? curr : prev;
  }, 1);
  if (closest < maxValue * 1.25 || closest > maxValue * 1.4) {
    closest = FINE_GRAINED_AXIS_VALUES.filter(
      (el) => el > maxValue * 1.25
    ).reduce(function (prev, curr) {
      return Math.abs(curr - maxValue) < Math.abs(prev - maxValue)
        ? curr
        : prev;
    }, 0);
  }
  return Math.max(1, closest);
};

const getChartData = ({
  data,
  comparedData,
  nvrKey,
}: {
  data: DailyMetric[];
  comparedData?: DailyMetric[];
  nvrKey: "total" | "new" | "returning";
}) => {
  let currentMaxValue = 0;
  let currentMaxRoasValue = 0;
  const chartDataByDate: Record<string, Record<string, any>> = {};

  // First, populate with main data
  for (const entry of data) {
    chartDataByDate[entry.date] = {
      date: entry.date,
    };

    chartDataByDate[entry.date].customer_revenue =
      entry[nvrKey].customer_revenue ?? 0;
    chartDataByDate[entry.date].total_spend = entry[nvrKey].total_spend ?? 0;
    chartDataByDate[entry.date].return_on_ad_spend =
      entry[nvrKey].return_on_ad_spend ?? 0;

    if (
      Math.max(
        entry[nvrKey].customer_revenue ?? 0,
        entry[nvrKey].total_spend ?? 0
      ) > currentMaxValue
    ) {
      currentMaxValue = Math.max(
        entry[nvrKey].customer_revenue ?? 0,
        entry[nvrKey].total_spend ?? 0
      );
    }
    if (entry[nvrKey].return_on_ad_spend > currentMaxRoasValue) {
      currentMaxRoasValue = entry[nvrKey].return_on_ad_spend;
    }
  }

  // Handle compared data with index-based alignment
  if (comparedData?.length && data?.length > 0) {
    const comparisonMap = alignComparisonDataByIndex(data, comparedData);

    for (const currentEntry of data) {
      const comparedEntry = comparisonMap.get(currentEntry.date);
      if (comparedEntry && chartDataByDate[currentEntry.date]) {
        chartDataByDate[currentEntry.date].compared_customer_revenue =
          comparedEntry[nvrKey].customer_revenue ?? 0;
        chartDataByDate[currentEntry.date].compared_total_spend =
          comparedEntry[nvrKey].total_spend ?? 0;
        chartDataByDate[currentEntry.date].compared_return_on_ad_spend =
          comparedEntry[nvrKey].return_on_ad_spend ?? 0;

        if (
          Math.max(
            comparedEntry[nvrKey].customer_revenue ?? 0,
            comparedEntry[nvrKey].total_spend ?? 0
          ) > currentMaxValue
        ) {
          currentMaxValue = Math.max(
            comparedEntry[nvrKey].customer_revenue ?? 0,
            comparedEntry[nvrKey].total_spend ?? 0
          );
        }
        if (comparedEntry[nvrKey].return_on_ad_spend > currentMaxRoasValue) {
          currentMaxRoasValue = comparedEntry[nvrKey].return_on_ad_spend;
        }
      }
    }
  }

  return {
    chartData: Object.values(chartDataByDate).sort((a, b) =>
      dayjs(a.date).diff(dayjs(b.date))
    ),
    maxValue: currentMaxValue,
    maxRoasValue: currentMaxRoasValue,
  };
};

type Props = {
  data: DailyMetric[];
  comparedData?: DailyMetric[];
  channel: string;
  suffix: string;
  onValueConversion: (
    value: number | undefined,
    numDigits: number,
    symbol?: boolean
  ) => string;
  granularity?: Granularity;
};

/**
 * Component holding the revenue chart info.
 *
 * @param {Props} props
 * @return {CustomElement}
 */
export function ConversionValueChart({
  data,
  comparedData,
  channel,
  suffix,
  onValueConversion,
  granularity = "daily",
}: Props) {
  const [dashboardMode] = useAtom(dashboardModeAtom);
  const spendColor =
    channel === "google"
      ? "#DB4437"
      : channel === "tiktok"
        ? "#25F4EE"
        : channel === "pinterest"
          ? "#E60023"
          : channel === "outbrain"
            ? "#ee6513"
            : "#0668E1";

  const { compared, startTime, endTime } = useDateRangeState();
  const { newVsReturning: nvrKey } = useNewVsReturningState();
  const { formatNumber } = useFormat();
  const isMobile = useIsMobile();

  const { chartData, maxValue, maxRoasValue } = getChartData({
    data,
    comparedData,
    nvrKey,
  });

  // Build chart config
  const chartConfig = useMemo<ChartConfig>(() => {
    const revenueLabel =
      dashboardMode === "general"
        ? GENERAL_METRICS_LABELS["customer_revenue"] || "Conv. Value"
        : METRICS_LABELS["customer_revenue"] || "Revenue";
    const spendLabel =
      dashboardMode === "general"
        ? GENERAL_METRICS_LABELS["total_spend"] || "Spend"
        : METRICS_LABELS["total_spend"] || "Spend";

    return {
      customer_revenue: {
        label: revenueLabel,
        color: "#6254FF",
      },
      compared_customer_revenue: {
        label: `Previous ${revenueLabel}`,
        color: "#6254FF",
      },
      total_spend: {
        label: spendLabel,
        color: spendColor,
      },
      compared_total_spend: {
        label: `Previous ${spendLabel}`,
        color: spendColor,
      },
      return_on_ad_spend: {
        label: "ROAS",
        color: "#DAA702",
      },
      compared_return_on_ad_spend: {
        label: "Previous ROAS",
        color: "#DAA702",
      },
    };
  }, [dashboardMode, spendColor]);

  const [xAxisTicks, setXAxisTicks] = useState(data.map((el) => el.date));
  useEffect(() => {
    function setXAxisTicksHandler() {
      const revenueChart = document.getElementById(
        "conversion-value-chart-container"
      );
      const width = revenueChart?.clientWidth ?? 0;
      // Adjust calculation for mobile - less space available
      const minWidth = isMobile ? 200 : 300;
      const maxAmountOfTicks = Math.floor((width - minWidth) / (36 + 15)); // the labels are roughly 36px wide + a 15px spacing
      const dates = data.map((el) => el.date);
      const dateKeys = dates.length;
      const keysIndexToChoose = Math.ceil(dateKeys / maxAmountOfTicks);
      const keysToDisplay = dates
        .map((el, index) => (index % keysIndexToChoose === 0 ? el : null))
        .filter((el) => Boolean(el)) as string[];
      setXAxisTicks(keysToDisplay);
    }
    setXAxisTicksHandler();
    window.addEventListener("resize", setXAxisTicksHandler);
    return () => {
      window.removeEventListener("resize", setXAxisTicksHandler);
    };
  }, [data, isMobile]);

  const formatTooltipLabel = (label: string | number | undefined) => {
    if (!label) {
      return "";
    }

    const labelValue = String(label);
    const firstDate = chartData[0]?.date;
    const lastDate = chartData[chartData.length - 1]?.date;

    return formatGranularityTooltipLabel({
      date: labelValue,
      granularity,
      isFirstTick: labelValue === firstDate,
      isLastTick: labelValue === lastDate,
      startDate: startTime,
      endDate: endTime,
    });
  };

  // Value formatter for tooltip - handles different metrics differently
  const formatValue = (value: number, key: string) => {
    const baseKey = key?.replace("compared_", "") || "";
    if (baseKey === "return_on_ad_spend") {
      return formatNumber(value, 1);
    }
    return onValueConversion(value, 2) || "";
  };

  const maxRoasDomain = getNearestAxisValue(maxRoasValue);
  const maxYDomain = getNearestAxisValue(maxValue);
  const primaryYAxisTicks = useMemo(
    () => Array.from({ length: 6 }, (_, index) => (maxYDomain / 5) * index),
    [maxYDomain]
  );

  const chartMargin = isMobile
    ? { top: 20, bottom: 5, right: 0, left: 0 }
    : { top: 20, bottom: 20, right: 0, left: 0 };

  const chartHeight = isMobile ? 300 : "100%";

  return (
    <div className="w-full h-full flex flex-col">
      <ChartContainer
        config={chartConfig}
        className={`w-full flex-1 ${isMobile ? "min-h-[300px]" : ""}`}
        id="conversion-value-chart-container"
      >
        <ComposedChart
          data={chartData}
          margin={chartMargin}
          height={typeof chartHeight === "number" ? chartHeight : undefined}
        >
          <YAxis
            hide={isMobile}
            tickFormatter={(value: number) =>
              `${onValueConversion(value, 0, false)}`
            }
            tickLine={false}
            tickSize={0}
            allowDataOverflow
            yAxisId="1"
            tickMargin={10}
            axisLine={false}
            tickCount={6}
            ticks={primaryYAxisTicks}
            className="relative"
            domain={[0, maxYDomain]}
          >
            <Label
              value={`Spend | ${
                dashboardMode === "general" ? "Conv. Value" : "Revenue"
              } in ${suffix}`}
              offset={isMobile ? 10 : 20}
              angle={-90}
              textAnchor="middle"
              position={{
                x: isMobile ? 10 : -20,
                y: 225,
              }}
              style={{ fontSize: isMobile ? "11px" : "12px" }}
            />
          </YAxis>
          <YAxis
            hide={isMobile}
            orientation="right"
            yAxisId="2"
            tickMargin={10}
            tickLine={false}
            axisLine={false}
            tickCount={maxRoasDomain % 5 === 0 ? 6 : 2}
            domain={[0, maxRoasDomain]}
          >
            <Label
              value={"ROAS"}
              angle={-90}
              position={"right"}
              offset={isMobile ? -10 : -15}
              textAnchor={"middle"}
              style={{ fontSize: isMobile ? "11px" : "12px" }}
            />
          </YAxis>
          <XAxis
            dataKey="date"
            tickSize={0}
            tickMargin={20}
            height={35}
            axisLine={false}
            interval={0}
            textAnchor="start"
            tick={(props) => (
              <XAxisTicksWithYear
                rechartsProps={props}
                xAxisTicks={xAxisTicks}
                startDate={startTime}
                endDate={endTime}
                granularity={granularity}
              />
            )}
            ticks={xAxisTicks}
          />
          <CartesianGrid
            stroke="var(--chart-grid)"
            vertical={false}
            yAxisId="1"
            horizontalValues={primaryYAxisTicks}
          />

          <ChartTooltip
            position={{ y: 50 }}
            content={(props) => (
              <ComparisonChartTooltipContent
                payload={props.payload}
                label={props.label}
                config={chartConfig}
                isCompared={compared}
                sortOrder={[
                  "customer_revenue",
                  "total_spend",
                  "return_on_ad_spend",
                ]}
                labelFormatter={formatTooltipLabel}
                valueFormatter={(v, key) => formatValue(v, key || "")}
              />
            )}
          />

          <Line
            type="monotone"
            yAxisId="1"
            dataKey="customer_revenue"
            strokeWidth={2}
            stroke="var(--color-customer_revenue)"
            fillOpacity={1}
            dot={false}
          />
          {compared ? (
            <Line
              type="monotone"
              yAxisId="1"
              dataKey="compared_customer_revenue"
              stroke="var(--color-customer_revenue)"
              dot={false}
              strokeDasharray={"5 5"}
              fillOpacity={1}
              strokeWidth={2}
            />
          ) : null}
          <Line
            type="monotone"
            yAxisId="1"
            dataKey="total_spend"
            strokeWidth={2}
            stroke="var(--color-total_spend)"
            fillOpacity={1}
            dot={false}
          />
          {compared ? (
            <Line
              type="monotone"
              yAxisId="1"
              dataKey="compared_total_spend"
              stroke="var(--color-total_spend)"
              dot={false}
              strokeDasharray={"5 5"}
              fillOpacity={1}
              strokeWidth={2}
            />
          ) : null}
          <Line
            type="monotone"
            yAxisId="2"
            dataKey="return_on_ad_spend"
            stroke="var(--color-return_on_ad_spend)"
            dot={false}
            strokeWidth={2}
          />
          {compared ? (
            <Line
              type="monotone"
              yAxisId="2"
              dataKey="compared_return_on_ad_spend"
              stroke="var(--color-return_on_ad_spend)"
              dot={false}
              strokeDasharray={"5 5"}
              strokeWidth={2}
            />
          ) : null}

          <ChartLegend
            content={(props) => (
              <ComparisonChartLegendContent
                payload={props.payload}
                config={chartConfig}
                isCompared={compared}
                sortOrder={[
                  "customer_revenue",
                  "total_spend",
                  "return_on_ad_spend",
                ]}
              />
            )}
          />
        </ComposedChart>
      </ChartContainer>
    </div>
  );
}
